import { CodingReviewMode } from "../../../../project/project-config/coding-review-mode.enum";
import { OverreadFeedbackQueryItem } from "./overread-feedback-query-item.model";
import { OverreadFeedbackStatus } from "./overread-feedback-status.enum";

/**
 * Shared filtering for the Overread Feedback / Acknowledgements views.
 *
 * The feedback grid, the My Chases header count and the data entry gate all have to agree on which
 * items a user still owes work on. They each used to carry their own copy of these predicates,
 * which is how the NextGen Risk (DiagnosisV3) rules ended up applied to the grid only - leaving the
 * header count, and therefore the gate derived from it, short on those projects.
 */
export class OverreadFeedbackFilter {

  /**
   * Narrows a feedback list to the items that concern the signed in coder or overreader, as opposed
   * to the ones a manager reviews.
   */
  static forUser(items: OverreadFeedbackQueryItem[], userId: number): OverreadFeedbackQueryItem[] {
    return items.filter(x => x.chaseOverreadFeedbackStatus !== OverreadFeedbackStatus.MANAGER && ((
      x.codingUserId === userId && (
        x.codingReceiptStatus === OverreadFeedbackStatus.REQUESTED ||
        x.codingReceiptStatus === OverreadFeedbackStatus.REQUESTEDNOTREQUIRED ||
        x.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.CODING) || x.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.COMPLETED) || (
        x.qaUserId === userId &&
        x.qaReceiptStatus === OverreadFeedbackStatus.REQUESTED || x.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.COMPLETED)
      || (x.qaUserId === userId && x.codingReviewMode === CodingReviewMode.DIAGNOSIS_V3)
      || (x.codingUserId === userId && x.codingReviewMode === CodingReviewMode.DIAGNOSIS_V3))
    );
  }

  /**
   * Items the user has not finished reviewing or acknowledging yet. NextGen Risk keeps its own
   * conditions so the Risk 2.0/2.1 behaviour is left untouched.
   */
  static incomplete(items: OverreadFeedbackQueryItem[], userId: number): OverreadFeedbackQueryItem[] {
    return items.filter(x =>
      x.chaseOverreadFeedbackStatus !== OverreadFeedbackStatus.COMPLETED && x.codingReviewMode !== CodingReviewMode.DIAGNOSIS_V3 ||
      (
        x.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.COMPLETED && x.codingReviewMode !== CodingReviewMode.DIAGNOSIS_V3 && (
          (x.codingReceiptStatus === OverreadFeedbackStatus.REQUESTED && x.codingUserId === userId) ||
          (x.qaReceiptStatus === OverreadFeedbackStatus.REQUESTED && x.qaUserId === userId)
        ))
      ||
      (x.chaseOverreadFeedbackStatus !== OverreadFeedbackStatus.COMPLETED && x.codingReviewMode === CodingReviewMode.DIAGNOSIS_V3 && (
        (x.qaUserId === userId) || (x.codingUserId === userId)))
      ||
      (x.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.COMPLETED && x.codingReviewMode === CodingReviewMode.DIAGNOSIS_V3 &&
        (x.codingReceiptStatus === OverreadFeedbackStatus.REQUESTED || x.codingReceiptStatus === OverreadFeedbackStatus.REQUESTEDNOTREQUIRED) && (
          (x.qaUserId === userId) || (x.codingUserId === userId))));
  }

  /**
   * Items where the review is done and the other party has already acknowledged.
   */
  static complete(items: OverreadFeedbackQueryItem[], userId: number): OverreadFeedbackQueryItem[] {
    return items.filter(x => x.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.COMPLETED &&
      ((x.codingUserId === userId && x.codingReceiptStatus === OverreadFeedbackStatus.RECEIVED && x.qaReceiptStatus === OverreadFeedbackStatus.REQUESTED)
        || (x.qaUserId === userId && x.qaReceiptStatus === OverreadFeedbackStatus.RECEIVED && x.codingReceiptStatus === OverreadFeedbackStatus.REQUESTED)
        || (x.qaUserId === userId && x.codingReviewMode === CodingReviewMode.DIAGNOSIS_V3 && x.codingReceiptStatus === OverreadFeedbackStatus.RECEIVED)
        || (x.codingUserId === userId && x.codingReviewMode === CodingReviewMode.DIAGNOSIS_V3 && x.codingReceiptStatus === OverreadFeedbackStatus.RECEIVED)));
  }

  /**
   * True when the user owes an acknowledgement that has to be cleared before they may start data
   * entry. A receipt status of RequestedNotRequired means the project has the requirement turned
   * off, so those items never block. Expects the incomplete list.
   */
  static requiresAcknowledgment(incompleteItems: OverreadFeedbackQueryItem[], userId: number): boolean {
    return incompleteItems.some(x =>
      x.codingUserId === userId && x.codingReceiptStatus !== OverreadFeedbackStatus.REQUESTEDNOTREQUIRED);
  }
}
